import { Navigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import usePaginate from "@/components/paginate/usePaginate";
import { useQuery } from "@tanstack/react-query";
import { getStreamlitNotebookJobChildQueries } from "@/helpers/apis";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { RelativeComponentLoader } from "@/components/loader";
import { Stack } from "@altimateai/lego";
import { ArrowRightIcon } from "@assets/icons";
import { CommonTaskHistoryTable } from "@/pages/query/common/tables/CommonTaskHistoryTable";
import { WorkloadType } from "@/pages/query/types";
import { toast } from "@altimateai/lego";
import { useBreadcrumb, getEntityName } from "@/components/breadcrumbs";

export const NotebooksTaskHistory = () => {
  const { sessionId } = useParams();
  const { state } = useLocation();
  const { startDate, endDate } = useDateFromSearchParams();
  const { page, setPage, pageSize, updatePageSize } = usePaginate();
  const [searchParams] = useSearchParams();

  useBreadcrumb(getEntityName(state) ?? sessionId ?? "Task History", {
    defaultParents: [
      { label: "Code", pathname: "/query" },
      { label: "Notebooks", pathname: "/query/notebooks" },
    ],
  });

  const { data, isLoading } = useQuery({
    queryKey: [
      "notebook_jobs",
      "notebook_runs",
      "notebook_tasks",
      sessionId,
      startDate,
      endDate,
      page,
      pageSize,
    ],
    queryFn: () =>
      getStreamlitNotebookJobChildQueries(encodeURIComponent(sessionId!), {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        page: page + 1,
        size: pageSize,
        workload_type: WorkloadType.NOTEBOOK,
      }),
    enabled: Boolean(sessionId),
  });

  if (!sessionId) {
    return <Navigate to="/query/streamlit" />;
  }
  if (isLoading) {
    return <RelativeComponentLoader label="Loading..." />;
  }
  if (!data) {
    // If no data is found, redirect to the main notebooks page
    toast({
      variant: "destructive",
      description: "No run history found for this notebook job.",
    });
    return <Navigate to="/query/streamlit" />;
  }
  return (
    <Stack direction="column" gap={"xl"}>
      <Stack align={"center"} gap={"sm"}>
        <span className="al-text-xl al-text-light-gray">Run History</span>
        <div className="al-size-5 al-text-light-gray">
          <ArrowRightIcon />
        </div>
        <span className="al-text-xl">{searchParams.get("run_query") || sessionId}</span>
      </Stack>
      <CommonTaskHistoryTable
        tableData={data}
        page={page}
        pageSize={pageSize}
        setPage={setPage}
        updatePageSize={updatePageSize}
      />
    </Stack>
  );
};
